import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { INVENTED_AUDIO_COPY } from '../../platform/config/audio';
import {
  CAMERA_MODE_COPY,
  INVENTED_CAMERA_SETUP_COPY,
  JUMP_SUBSTITUTE_COPY,
} from '../../platform/config/camera-mode';
import {
  INVENTED_COOPERATIVE_READINESS_COPY,
  READINESS_MESSAGES,
} from '../../platform/config/camera-readiness';
import {
  CAMERA_MODES,
  JUMP_SUBSTITUTES,
  type CameraMode,
  type JumpSubstitute,
} from '../../shared/types/camera-mode';
import { SKILL_BANDS, type SkillBand } from '../../shared/types/ids';
import type { ChildProfile } from '../../shared/types/profiles';
import type { SessionLaunchConfig } from '../../shared/types/session-launch';
import { loadSessionLaunchDefaults, resolveSecondPlayerDefaults } from '../session/launch-defaults';
import { useAppServices } from '../shell/services-context';

/**
 * Parent setup for one sitting (game design spec §11, MVP notes §20).
 *
 * Everything here is session-only. The form seeds itself from the child's stored preferences,
 * then the family's, then the built-in defaults, and it never writes any of them back: a level
 * chosen because a child is tired tonight is not a change to who that child is. Profile editing
 * lives on the players and parent screens.
 *
 * It is also where the sitting's roster is decided — one player or two heroes together, and who
 * the second hero is. Nowhere else offers that choice: lanes, the saved-mission slot and each
 * child's evidence all follow the players a mission started with, so the mode is fixed for the
 * whole sitting (D-037(1), PS2-7).
 *
 * Scope is deliberate, and it moved in Phase 3. GDS §11's **camera configuration** and **jump
 * substitute** are here now, because there is finally something behind them (D-037(3)): the play
 * space decides what hero training measures and what a counting round counts. **Activity level is
 * still absent**, for the reason it always was — no document anywhere says what it would change,
 * and a control that does nothing is worse on a grown-up's screen than an honest omission.
 *
 * The camera-mode field is D-059's ruling made concrete: the readiness check *measures a distance
 * and recommends*, this field is seeded with that recommendation, and the parent may override it.
 * Compact is GDS §15's accessibility path, and an accessibility decision belongs to a grown-up
 * rather than to a heuristic that a lighting blip could flip halfway through a mission.
 *
 * Plain form controls throughout, so the screen is fully keyboard-navigable (ACC-03).
 */

/** The slider works in whole percent; the config and the narration service work in 0..1. */
const VOLUME_STEPS = 100;

/** GDS §12's own "…Mode Ready" wording, reused so the two screens name a mode identically. */
const MODE_READY_MESSAGE: Readonly<Record<CameraMode, string>> = {
  compact: READINESS_MESSAGES.compactReady,
  'full-motion': READINESS_MESSAGES.fullMotionReady,
};

const BAND_LABELS: Readonly<Record<SkillBand, string>> = {
  explorer: 'Explorer — gentle start',
  hero: 'Hero — steady challenge',
  master: 'Master — stretch it',
};

export function ParentSetupPage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { repositories, activePlayer, setSessionLaunchConfig, narration, camera, audio } =
    useAppServices();

  const [config, setConfig] = useState<SessionLaunchConfig | null>(null);
  const [siblings, setSiblings] = useState<ChildProfile[]>([]);
  /**
   * What the camera check suggested, read once when the form seeds itself.
   *
   * Read once rather than subscribed to on purpose: a recommendation that changed under a grown-up
   * mid-form would move a radio button they had just chosen. The camera is not even running on this
   * screen — this is the measurement from when the child was standing in front of it.
   */
  const [recommendedMode] = useState<CameraMode | null>(() => camera.recommendedCameraMode);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [defaults, children] = await Promise.all([
        loadSessionLaunchDefaults(repositories, activePlayer, recommendedMode),
        repositories.profiles.listChildren(),
      ]);
      if (cancelled) return;
      setConfig(defaults);
      // The first hero is already playing, so offering their name again would let one child be
      // put in both lanes — two slots, one profile, and evidence for a sitting they played once.
      setSiblings(children.filter((child) => child.id !== activePlayer?.profileId));
    })();
    return () => {
      cancelled = true;
    };
  }, [repositories, activePlayer, recommendedMode]);

  if (gameId === undefined) {
    return (
      <section className="error-screen" role="alert">
        <h1>No game was chosen</h1>
        <Link to="/catalogue" className="button button--primary">
          Back to games
        </Link>
      </section>
    );
  }

  if (config === null) {
    return (
      <section data-testid="setup-loading">
        <h1>Getting the settings ready…</h1>
      </section>
    );
  }

  const update = (patch: Partial<SessionLaunchConfig>): void => {
    setConfig({ ...config, ...patch });
  };

  const second = config.secondPlayer;
  const isCoop = config.playerMode === 'COOP_SIMULTANEOUS_SIMPLE';
  /** Cooperative camera play is Full-Motion only for the MVP (D-076(4)). */
  const offeredCameraModes = isCoop
    ? CAMERA_MODES.filter((mode) => mode !== 'compact')
    : CAMERA_MODES;
  /**
   * A Compact recommendation is real, and it is not a recommendation two heroes can act on: the
   * measurement was taken of one child in a small room. Suppressed rather than reworded, so the
   * field falls back to "no suggestion yet" instead of naming a play space this sitting cannot use.
   */
  const shownRecommendation = isCoop && recommendedMode === 'compact' ? null : recommendedMode;

  /**
   * Switching mode rewrites the roster rather than hiding half of it: going back to one player
   * removes the second hero entirely, so a choice a grown-up backed out of cannot reach the stage.
   */
  const chooseMode = (mode: SessionLaunchConfig['playerMode']): void => {
    if (mode === 'COOP_SIMULTANEOUS_SIMPLE') {
      // A guest until somebody is named: nothing on this screen may assume which child is here.
      update({
        playerMode: mode,
        secondPlayer: config.secondPlayer ?? resolveSecondPlayerDefaults(null),
        /**
         * Cooperative camera play is Full-Motion only for the MVP (D-076(4)).
         *
         * Set here rather than filtered at the point of use, so a sitting that reaches the stage
         * carries the truth: GDS §6/MIN §6 pair cooperative play exclusively with the wider band,
         * and a Compact coop sitting would be asking two children to share the space one child
         * stands in. A family whose room cannot fit two lanes plays cooperatively at the keyboard.
         */
        cameraMode: 'full-motion',
      });
      return;
    }

    const { secondPlayer: _dropped, ...rest } = config;
    setConfig({ ...rest, playerMode: mode });
  };

  const chooseSecondPlayer = (profileId: string): void => {
    const child = siblings.find((candidate) => candidate.id === profileId) ?? null;
    update({ secondPlayer: resolveSecondPlayerDefaults(child) });
  };

  /**
   * The sitting as the rest of the app will read it.
   *
   * An emptied name field means "no name", not an empty one — so the property is rebuilt rather
   * than overwritten, and the lane label takes over downstream.
   */
  const commit = (): void => {
    const { playerLabel: typed, ...rest } = config;
    const label = typed?.trim() ?? '';
    setSessionLaunchConfig({
      ...rest,
      ...(label.length === 0 ? {} : { playerLabel: label }),
    });

    /**
     * The audio unlock, riding the gesture that already exists (PS5-5, D-094(6)).
     *
     * Every browser's autoplay policy is satisfied by a user gesture and by nothing else, and the
     * standard answer — a "tap to enable sound" panel — would put a piece of browser policy on a
     * screen a five-year-old's grown-up is trying to get past. This *is* the gesture: they pressed
     * the button that starts the mission. Both ways off this screen go through `commit`, so the
     * one that detours via the camera check unlocks too.
     *
     * The service is told the sitting's choices first, so a muted sitting is muted from the moment
     * it is capable of sound rather than for the length of one loop's first note. Nothing plays
     * here: unlocking is permission, not playback.
     */
    audio.setEnabled(config.soundEnabled);
    audio.setVolume(config.soundVolume);
    void audio.unlock();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    commit();
    void navigate(`/play/${gameId}`);
  };

  /**
   * MIN §8's order, for the one case that needs it: `Parent Setup → Hardware Readiness`.
   *
   * A cooperative readiness check can only exist downstream of this screen, because the width it
   * checks for exists *because* two heroes were chosen and nothing else in the app knows that. The
   * sitting is committed on the way through, so the readiness screen reads the same
   * `SessionLaunchConfig` the stage would — one holder, one answer — and the mission can be started
   * from there without a grown-up setting the same sitting up twice.
   */
  const openCameraCheck = (): void => {
    commit();
    void navigate(`/play/${gameId}/camera-check`);
  };

  return (
    <section>
      <h1>Before you start</h1>
      <p className="lede">Settings for this game only. Children never see this screen.</p>

      <form
        className="panel"
        onSubmit={handleSubmit}
        aria-label="Session setup"
        data-testid="parent-setup"
      >
        {/* Chosen here and only here: the roster is fixed for the whole sitting, because lanes,
            the saved-mission slot and each child's evidence all follow the players a mission
            started with (D-037(1)). */}
        <fieldset className="field" data-testid="setup-player-mode">
          <legend>Who is playing</legend>
          <label>
            <input
              type="radio"
              name="session-player-mode"
              value="SINGLE"
              checked={config.playerMode === 'SINGLE'}
              onChange={() => {
                chooseMode('SINGLE');
              }}
            />{' '}
            One player
          </label>
          <br />
          <label>
            <input
              type="radio"
              name="session-player-mode"
              value="COOP_SIMULTANEOUS_SIMPLE"
              checked={config.playerMode === 'COOP_SIMULTANEOUS_SIMPLE'}
              onChange={() => {
                chooseMode('COOP_SIMULTANEOUS_SIMPLE');
              }}
            />{' '}
            Two heroes, playing together
          </label>
          {config.playerMode === 'COOP_SIMULTANEOUS_SIMPLE' ? (
            <p className="notice">
              Both heroes see the same question and can answer at the same time. The shards are
              shared, and the game never says which child answered.
            </p>
          ) : null}
        </fieldset>

        {config.playerMode === 'COOP_SIMULTANEOUS_SIMPLE' ? (
          <>
            <div className="field">
              <label htmlFor="session-second-player">Who is playing with them?</label>
              <select
                id="session-second-player"
                name="session-second-player"
                value={second?.profileId ?? ''}
                onChange={(event) => {
                  chooseSecondPlayer(event.target.value);
                }}
              >
                <option value="">A guest — nothing is saved for them</option>
                {siblings.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.nickname}
                  </option>
                ))}
              </select>
              <p className="notice">
                A saved player keeps their own progress. A guest plays along and keeps nothing.
              </p>
            </div>

            <div className="field">
              <label htmlFor="session-second-band">Level for the second hero</label>
              <select
                id="session-second-band"
                name="session-second-band"
                value={second?.band ?? config.band}
                onChange={(event) => {
                  update({
                    secondPlayer: {
                      ...(second ?? resolveSecondPlayerDefaults(null)),
                      band: event.target.value as SkillBand,
                    },
                  });
                }}
              >
                {SKILL_BANDS.map((option) => (
                  <option key={option} value={option}>
                    {BAND_LABELS[option]}
                  </option>
                ))}
              </select>
              <p className="notice">
                Each hero starts at their own level. The game picks questions the pair can both take
                on, and never shows why.
              </p>
            </div>
          </>
        ) : null}

        <div className="field">
          <label htmlFor="session-band">
            {config.playerMode === 'COOP_SIMULTANEOUS_SIMPLE'
              ? 'Level for the first hero'
              : 'Level for this session'}
          </label>
          <select
            id="session-band"
            name="session-band"
            value={config.band}
            onChange={(event) => {
              update({ band: event.target.value as SkillBand });
            }}
          >
            {SKILL_BANDS.map((option) => (
              <option key={option} value={option}>
                {BAND_LABELS[option]}
              </option>
            ))}
          </select>
          <p className="notice">
            Only for this game. The player&rsquo;s saved level stays as it is.
          </p>
        </div>

        <div className="field">
          <label htmlFor="session-name">Name shown in the game (optional)</label>
          <input
            id="session-name"
            name="session-name"
            autoComplete="off"
            value={config.playerLabel ?? ''}
            onChange={(event) => {
              update({ playerLabel: event.target.value });
            }}
          />
        </div>

        <label>
          <input
            type="checkbox"
            checked={config.narrationEnabled}
            onChange={(event) => {
              update({ narrationEnabled: event.target.checked });
            }}
          />{' '}
          Narration (Bip-Bop reads the instructions aloud)
        </label>
        <p className="notice" data-testid="setup-narration-availability">
          {narration.available
            ? 'Your browser can speak the instructions.'
            : 'This browser has no speech voice available, so narration stays silent.'}
        </p>

        <div className="field">
          <label htmlFor="session-volume">Narration volume</label>
          <input
            id="session-volume"
            name="session-volume"
            type="range"
            min={0}
            max={VOLUME_STEPS}
            step={5}
            value={Math.round(config.narrationVolume * VOLUME_STEPS)}
            onChange={(event) => {
              update({ narrationVolume: Number(event.target.value) / VOLUME_STEPS });
            }}
          />
        </div>

        {/*
         * Sound, beside narration because they are the two things that come out of the speakers
         * and a grown-up deciding about one is deciding about the other (PS5-5).
         *
         * Separate controls, deliberately: narration is the instruction a child needs to hear, and
         * music is atmosphere. A family with a sleeping baby turns one of them off; a family whose
         * child is hard of hearing turns off the other. One combined switch would force a choice
         * neither of them wants to make.
         */}
        <label>
          <input
            type="checkbox"
            checked={config.soundEnabled}
            onChange={(event) => {
              update({ soundEnabled: event.target.checked });
            }}
          />{' '}
          {INVENTED_AUDIO_COPY.soundLabel}
        </label>
        <p className="notice">{INVENTED_AUDIO_COPY.soundNote}</p>

        <div className="field">
          <label htmlFor="session-sound-volume">{INVENTED_AUDIO_COPY.soundVolumeLabel}</label>
          <input
            id="session-sound-volume"
            name="session-sound-volume"
            type="range"
            min={0}
            max={VOLUME_STEPS}
            step={5}
            value={Math.round(config.soundVolume * VOLUME_STEPS)}
            onChange={(event) => {
              update({ soundVolume: Number(event.target.value) / VOLUME_STEPS });
            }}
          />
        </div>

        <label>
          <input
            type="checkbox"
            checked={config.reducedMotion}
            onChange={(event) => {
              update({ reducedMotion: event.target.checked });
            }}
          />{' '}
          Calmer motion (fewer moving effects)
        </label>

        {/*
         * GDS §11's "Camera configuration", returning (D-037(3)).
         *
         * Radios rather than a select because there are exactly two and both deserve their
         * explanation on screen: a grown-up choosing a play space is choosing how much room their
         * child needs, and hiding the second option behind a dropdown makes the accessibility path
         * the harder one to find. Neither option is described as better, or as being for a child
         * who cannot do something.
         */}
        <fieldset className="field" data-testid="setup-camera-mode">
          <legend>{INVENTED_CAMERA_SETUP_COPY.modeLegend}</legend>
          {/*
           * Compact is absent for two heroes, and the reason is on the screen (D-076(4)).
           *
           * GDS §6 lists cooperative play under Full-Motion's use list and MIN §6 pairs it with the
           * wider distance band; GDS §12's own "Cooperative Mode Needs More Width" is that band's
           * floor. Saying so is the point — a grown-up whose option quietly vanished would assume
           * they had done something wrong, and the alternative they actually have (both heroes at
           * the keyboard) is worth naming rather than leaving them to find.
           */}
          {isCoop ? (
            <p className="notice" data-testid="setup-coop-camera-note">
              {INVENTED_COOPERATIVE_READINESS_COPY.fullMotionOnly}{' '}
              {INVENTED_COOPERATIVE_READINESS_COPY.keyboardInstead}
            </p>
          ) : null}
          {offeredCameraModes.map((mode) => (
            <p key={mode} style={{ margin: '0.25rem 0' }}>
              <label>
                <input
                  type="radio"
                  name="session-camera-mode"
                  value={mode}
                  checked={config.cameraMode === mode}
                  onChange={() => {
                    update({ cameraMode: mode });
                  }}
                />{' '}
                {CAMERA_MODE_COPY[mode].label}
              </label>
              <br />
              <span className="lede">{CAMERA_MODE_COPY[mode].detail}</span>
            </p>
          ))}
          <p className="notice" data-testid="setup-camera-recommendation">
            {shownRecommendation === null
              ? INVENTED_CAMERA_SETUP_COPY.noRecommendation
              : `${INVENTED_CAMERA_SETUP_COPY.recommendationPrefix} ${MODE_READY_MESSAGE[shownRecommendation]}.`}
          </p>
          {/* MIN §8's `Parent Setup → Hardware Readiness`, for the sitting that needs it. */}
          {isCoop ? (
            <p>
              <button
                type="button"
                className="button"
                data-testid="setup-coop-camera-check"
                onClick={openCameraCheck}
              >
                {INVENTED_CAMERA_SETUP_COPY.coopCameraCheck}
              </button>
            </p>
          ) : null}
        </fieldset>

        {/*
         * GDS §11's "Jump substitute", returning with the mode that gives it an effect.
         *
         * Shown only for Compact, because in Full-Motion there is nothing to substitute — the
         * counting rounds count jumps. Switching back to Full-Motion keeps the value rather than
         * discarding it, so a grown-up who changes their mind twice does not lose their choice.
         * The mid-mission "switch substitute" override is Slice G; this is the setup screen, and
         * D-037(1) keeps the two apart.
         */}
        {config.cameraMode !== 'compact' ? null : (
          <div className="field" data-testid="setup-jump-substitute">
            <label htmlFor="session-jump-substitute">
              {INVENTED_CAMERA_SETUP_COPY.substituteLabel}
            </label>
            <select
              id="session-jump-substitute"
              name="session-jump-substitute"
              value={config.jumpSubstitute}
              onChange={(event) => {
                update({ jumpSubstitute: event.target.value as JumpSubstitute });
              }}
            >
              {JUMP_SUBSTITUTES.map((substitute) => (
                <option key={substitute} value={substitute}>
                  {JUMP_SUBSTITUTE_COPY[substitute]}
                </option>
              ))}
            </select>
            <p className="notice">{INVENTED_CAMERA_SETUP_COPY.substituteNote}</p>
          </div>
        )}

        {/* GLP-07: the short version of the privacy statement, where the decision to start is
            actually made. A grown-up launching from the catalogue would otherwise reach this
            button having been told about bands and volume and nothing about their child's data.
            Same register and same facts as the Parent page, which holds the full statement. */}
        <div className="notice" data-testid="setup-privacy-note">
          <strong>Privacy:</strong> everything runs on this device — no account, no server. Nothing
          is ever recorded: no video, no photos, no voice, no body measurements. When your child
          plays with the camera the picture is used to work out movement and is never recorded or
          uploaded. <Link to="/parent">Read the full statement</Link>.
        </div>

        <p>
          <button type="submit" className="button button--primary">
            Start the mission
          </button>
        </p>
      </form>

      <p>
        <Link to="/catalogue" className="button button--quiet">
          Back to games
        </Link>
      </p>
    </section>
  );
}
